import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { GlobalShadowStyleDirective, PtlNotificationService, PtlNotificationStyle, PtlWindowLocationService } from '@ptl/global-components';
import { StateStructure } from '@ptl/state';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { MassUploadGridService } from './services/mass-upload-grid.service';
import { ENTITLEMENTS, EntitlementsHelperService, LINE_OF_BUSINESS_ENUM, LOGIN_INFO_REDUCER_NAME, UserInfoStateStructure, USER_ROLE, BaseAgency } from "@ptl/account-management";
import * as XLSX from 'xlsx';
import { FormConfig, FormNavigationConfig } from '@ptl/forms';
import { FormConfigUtilityService } from '../../form-wrappers/services/filterByConfigMetatags/form-config-utility.service';
import { EXCEL_ENROLLMENT_TYPES, MassUploadEntryConfig, MassUploadEntryMap, MassUploadExcelToJsonMap, MassUploadStateFormMap, EXCEL_PERSON_TYPES, EXCEL_ROW_INDEX, MassUploadRowData, MassUploadGridConfiguration, PAYLOAD_TO_EXCEL_MAP, MassUploadRowAction, PAYLOAD_PERSON_TYPES } from './interfaces/mass-upload-grid.interface';
import { MassUploadGridDataHelperService } from './services/mug-data-helper.service';
import { NgRedux } from '@ptl/angular-redux-store';
import { clearAllStates } from './reducer/mass-upload-grid.reducer';
import { MassUploadGridFormHelperService } from './services/mug-form-helper.service';
import { MassUploadGridSubmissionDataService } from './services/mug-submission-data.service';
import { CASE_STATUS, EnrollmentRequest_MN_Variation, EnrollmentRequestPayload, EnrollmentResponsePayload, MassUploadApplicant, MassUploadApplicant_MN_Variation, ProxiedApplicantAttestationMassUploadApplicant, ProxiedApplicantAttestationMassUploadApplicantWithProducerFullName, ProxiedSpouseAttestationMassUploadApplicant, SUBMISSION_STATUS } from './interfaces/mug-submission.interface';
import { MassUploadGridCaseService } from './services/mug-case.service';
import { MassUploadGridEntryMapTransformerService } from './services/mug-entry-map-transformer.service';
import { CommonUtilityService } from 'src/app/shared/utility-services/common-utility.service';
import { ConsumerProfileComponent } from '../../user-profile/consumer-profile/consumer-profile.component';
import { MassUploadGridRowActionsService } from './services/mug-row-actions.service';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-mass-upload-grid',
    templateUrl: './mass-upload-grid.component.html',
    styleUrls: ['./mass-upload-grid.component.scss'],
    standalone: false,
    encapsulation: ViewEncapsulation.ShadowDom
})
export class MassUploadGridComponent extends GlobalShadowStyleDirective implements OnInit, AfterViewInit {

  defaultErrorMessages = {
    required: 'required',
    matDatepickerMin: 'Date is too recent',
    matDatepickerMax: 'Date is too old',
    matDatepickerDaysAgo: 'Date is out of range',
    agencyRegion: 'Current agency does not match',
    requiredValue: 'Invalid value'
  };

  VERSION_SHEET_NAME = 'Version';
  MAIN_SHEET_NAME = 'Main Sheet';

  @Input() templateVersion = 'QlVMS19URU1QTEFURV8yMDI2Lnhsc3g=';
  @Input() noEntitlementsMessage: string;
  @Input() massUploadGridConfigUrl: string;
  @Input() massUploadDashboardUrl: string = '/individual/tools/mug/dashboard';
  @Input() massUploadSubmissionUrl: string = '/individual/tools/mug/submission-log';

  @ViewChild('gridScrollContainer') gridScrollContainer: ElementRef<HTMLDivElement>;
  @ViewChild('tableBodyContainer') tableBodyContainer: ElementRef<HTMLDivElement>;

  massUploadGridConfig: MassUploadGridConfiguration;
  formJsonToExcelMap: MassUploadExcelToJsonMap[];
  placeholderFormConfig: FormConfig<FormNavigationConfig> = {replacementMap: {}} as FormConfig<FormNavigationConfig>; 

  gridHeaders: string[] = [];
  gridFormMap: MassUploadStateFormMap[] = [];
  gridContentList: string[][] = [];
  gridEntryList: MassUploadEntryConfig[][] = [];

  DATA_ROW_HEIGHT = 70;
  MAX_ROWS = 100;
  previousScrollTop = 0;
  visibleStartIndex = 0;
  visibleEndIndex = 0;
  actionMenuItems: MassUploadRowAction[] = [];

  caseId: string;
  userId: string;
  agency: BaseAgency;
  isSubmittedCase = false;
  hasEntitlement = false;
  isLoading = true;
  errorMessage: string;
  currentProcessedRows: number = 0;
  totalRowsToProcess: number = 0;

  constructor(
    private mugService: MassUploadGridService,
    private mugDataHelperService: MassUploadGridDataHelperService,
    private mugSubmissionDataService: MassUploadGridSubmissionDataService,
    private mugCaseService: MassUploadGridCaseService,
    private mugFormHelper: MassUploadGridFormHelperService,
    private mugEntryTransformerService: MassUploadGridEntryMapTransformerService,
    private mugRowActionService: MassUploadGridRowActionsService,
    private changeDetection: ChangeDetectorRef,
    private reducerUtilityService: ReducerUtilityService,
    private ngRedux: NgRedux<StateStructure>,
    private entitlementsHelperService: EntitlementsHelperService,
    private commonUtilityService: CommonUtilityService,
    private ptlNotificationService: PtlNotificationService,
    private ptlWindowService: PtlWindowLocationService
  ) {
    super();
  }

  async ngOnInit() {
    // sometimes on refresh the page is scrolled down to the bottom, this just makes sure we're loading the page at the top
    document.getElementsByTagName('html')[0].scrollTop = 0;

    const loginInfo: UserInfoStateStructure = this.reducerUtilityService.getReducerFromApplicationState(LOGIN_INFO_REDUCER_NAME);
    this.userId = loginInfo.userId;
    this.agency = loginInfo.userInfo.agencyInfo.selectedAgencies[LINE_OF_BUSINESS_ENUM.INDIVIDUAL];
    
    const loginData: UserInfoStateStructure = this.reducerUtilityService.getReducerFromApplicationState(LOGIN_INFO_REDUCER_NAME);
    const hasMassEnrollEntitlement = await this.entitlementsHelperService.entitlementValueForSelectedAgencyByNameAndLob(ENTITLEMENTS.MASS_UPLOAD, LINE_OF_BUSINESS_ENUM.INDIVIDUAL, loginData?.userInfo?.role as USER_ROLE);

    if (hasMassEnrollEntitlement) {
      this.hasEntitlement = true;
    } 
    else {
      this.isLoading = false;
      this.errorMessage = this.noEntitlementsMessage ?? 'Your currently selected agency does not have access to view Mass Upload. Try a different agency if available.';
      return;
    }

    if (!this.massUploadGridConfigUrl) {
      this.isLoading = false;
      this.errorMessage = 'No configuration provided in component';
      return;
    }

    this.massUploadGridConfig = await this.mugService.loadJsonExcelMap(this.massUploadGridConfigUrl);
    this.formJsonToExcelMap = this.massUploadGridConfig.formJsonToExcelMap;
    this.gridHeaders = this.formJsonToExcelMap.map((entry) => entry.excelHeading);

    // Load  all the necessary formsAsJson files and process them for use in the grid
    await this.mugRowActionService.initializeFormsAsJson(this.massUploadGridConfig, this.agency);
    // Certain row building actions require us to wait, so this communicates the isLoading value for the loading mask
    this.mugRowActionService.loadingStatus$.subscribe((isLoading) => {
      this.isLoading = isLoading;

      this.resetTableHeight();
      this.updateScrollableElements();
    });

    // Bind references to services so modifications are shared
    this.bindServiceVariableReferences();

    // TODO - is this necessary anymore?
    // Clear all MassUpload data
    this.ngRedux.dispatch(clearAllStates());


    // Load enrollments for the current case
    let url: URL = new URL(window.location.href);
    let params: URLSearchParams = url.searchParams;
    this.caseId = params.get('caseId');
    if (this.caseId) {
      await this.loadEnrollments();
    }

    this.isLoading = false;
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();

    this.gridScrollContainer.nativeElement.addEventListener('scroll', (event) => {
      // Don't update or blur if this is a horizontal scroll otherwise tabbing to next field will not work
      const scrollTop = this.gridScrollContainer.nativeElement.scrollTop;
      if (this.previousScrollTop === scrollTop) {
        return;
      }
      this.previousScrollTop = scrollTop;

      // Update the visible rows on vertical scroll
      this.updateScrollableElements();

      // Force blur on any potentially active inputs to save values (values are saved on blur)
      (document.activeElement as HTMLElement).blur();
    });
  }

  // By binding these array references together we can manipulate the same data and have it automatically
  // synchronized across all of the services that use that data.
  bindServiceVariableReferences() {
    this.mugFormHelper.formJsonToExcelMap = this.formJsonToExcelMap;
    this.mugDataHelperService.gridFormMap = this.gridFormMap;
    this.mugRowActionService.gridContentList = this.gridContentList;
    this.mugRowActionService.gridEntryList = this.gridEntryList;
    this.mugRowActionService.gridFormMap = this.gridFormMap;
  }

  onFileDropped(file: File): void {
    this.errorMessage = null;
    this.handleFile(file);
  }

  onInvalidFile(error: string): void {
    this.errorMessage = error;
    console.error(error);
  }

  async handleFile(file: File): Promise<void> {
    if (!file) {
      return;
    }

    this.errorMessage = null;
    this.isLoading = true;
    this.mugRowActionService.currentEditRowIndex = -1;
    this.totalRowsToProcess = 0;
    this.gridFormMap = [];
    this.gridEntryList = [];
    this.bindServiceVariableReferences();
    
    try {
      const reader = new FileReader();
      reader.onload = async (e: any) => {
        // Get file data as an XLSX workbook
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array', bookVBA: true });

        // Get the sheet with the versioning information
        const versionSheet = workbook.Sheets[this.VERSION_SHEET_NAME];
        const versionData = XLSX.utils.sheet_to_json(versionSheet, { header: 1 });

        // Get the sheet with the enrollment data
        const firstSheet = workbook.Sheets[this.MAIN_SHEET_NAME];
        const excelData: any = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });

        this.gridHeaders = excelData.splice(0, 1)[0];
        this.gridContentList = excelData.filter((lineItem: string[]) => lineItem?.length > 1);

        // If gridHeaders does not match our map length, this is most likely an invalid worksheet
        if (this.gridHeaders?.length !== this.formJsonToExcelMap.length) {
          this.isLoading = false;
          this.errorMessage = 'Invalid file detected. Please make sure you only upload the approved template.';
          return;
        }
        // Make sure excel template uploaded is the latest version
        else if (this.templateVersion !== 'any' && versionData[0]?.[0] !== this.templateVersion) {
          console.warn('uploaded excel version:', versionData[0], 'compare version', this.templateVersion);
          this.isLoading = false;
          this.errorMessage = 'You are not using the latest template. Please download a new one above and try again.';
          return;
        }
        // If content list is empty, we can show an error that no data was provided
        else if (!this.gridContentList?.length) {
          this.isLoading = false;
          this.errorMessage = 'No data provided in uploaded template.';
          return;
        }
        

        // Limit import to MAX_ROWS(100) rows of data
        if (this.gridContentList.length > this.MAX_ROWS) {
          this.errorMessage = `Template has more than ${this.MAX_ROWS} rows. Only the first ${this.MAX_ROWS} rows will be imported.`;
          this.gridContentList.splice(this.MAX_ROWS, this.gridContentList.length);
        }

        // Build an entry list and form controls for every data row
        this.totalRowsToProcess = this.gridContentList.length;

        for (let dataIndex = 0; dataIndex < this.gridContentList.length; dataIndex++) {
          await this.mugRowActionService.buildRow({rowData: this.gridContentList[dataIndex]}, dataIndex, dataIndex);
        }

        await this.loadAndInitializeAllDataAndValidation()

        // wait 2 more seconds for component cleanup to finish easily
        await new Promise(resolve => setTimeout(resolve, 2000));

        this.resetTableHeight();
        this.updateScrollableElements();

        this.isLoading = false;
        this.totalRowsToProcess = 0;
      };

      reader.readAsArrayBuffer(file);
    } 
    catch (error) {
      this.isLoading = false;
      console.error('Error: ', error);
      this.errorMessage = error;
    }
  }

  async loadAndInitializeAllDataAndValidation() {
    this.currentProcessedRows = 0;
    for (let i = 0; i < this.gridEntryList.length; i++) {
      this.currentProcessedRows++;
      await this.mugRowActionService.loadAndInitializeSingleEntryData(this.gridEntryList[i], i);
    }
  }
  
  changeEditRow(index: number) {
    if (this.isSubmittedCase) {
      return;
    }

    this.mugRowActionService.changeEditRow(index);
  }

  updateRowActions(rowIndex: number, $event: Event) {
    $event.preventDefault();
    $event.stopPropagation();
    this.actionMenuItems = this.mugRowActionService.getUpdatedRowActions(rowIndex, $event);
  }

  trackByStateId(entryRow: MassUploadEntryConfig[]): number {
    return entryRow[0]?.massUploadAttributes?.stateId;
  }

  async saveGridForLater() {
    this.errorMessage = null;
    this.isLoading = true;

    if (!this.caseId) {
      await this.createCaseId();
    }

    try {
      let submissionPayload = this.mugSubmissionDataService.buildSubmissionPayload(this.gridFormMap, this.agency, SUBMISSION_STATUS.PENDING);
      console.log(submissionPayload);
      await this.mugCaseService.saveEnrollmentsForCaseId(this.caseId, this.userId, this.agency.agencyId, submissionPayload);
      
      this.ptlNotificationService.open({
        title: `Saved - Case #${this.caseId}`,
        content: `Your enrollments have been saved!`,
        primaryButtonText: 'OK',
        notificationStyle: PtlNotificationStyle.SUCCESS
      });
    }
    catch (e) {
      console.error(e);
      this.ptlNotificationService.open({
        title: 'Error',
        content: 'An error occurred saving the enrollments.',
        primaryButtonText: 'OK',
        notificationStyle: PtlNotificationStyle.DANGER
      });
    }

    this.isLoading = false;
  }

  async submitAllApplications() {
    this.errorMessage = null;

    for (let gridIndex = 0; gridIndex < this.gridFormMap.length; gridIndex++) {
      if (!this.mugDataHelperService.isValidFormGroup(this.gridFormMap[gridIndex])){
        this.errorMessage = 'Not all rows are valid';
        return;
      }
    }

    this.isLoading = true;

    try {
      if (!this.caseId) {
        await this.createCaseId();
      }

      let submissionPayload = this.mugSubmissionDataService.buildSubmissionPayload(this.gridFormMap, this.agency, SUBMISSION_STATUS.SUBMITTED);
      console.log(submissionPayload);
      await this.mugCaseService.submitEnrollmentsForCaseId(this.caseId, this.userId, this.agency.agencyId, submissionPayload);   

      this.ptlNotificationService.open({
        title: `Submitted - Case #${this.caseId}`,
        content: `Your enrollments have been submitted. You will now be returned to the dashboard.`,
        primaryButtonText: 'OK',
        notificationStyle: PtlNotificationStyle.SUCCESS
      }).afterClosed().subscribe(() => {
        this.ptlWindowService.goToUrl(this.massUploadDashboardUrl);
      });
    }
    catch (e) {
      console.error(e);
      this.ptlNotificationService.open({
        title: 'Error',
        content: 'An error occurred submitting the enrollments.',
        primaryButtonText: 'OK',
        notificationStyle: PtlNotificationStyle.DANGER
      });
    }

    this.isLoading = false;
  }

  // When this creates a case id, it auto adds the query param and changes the history
  // So a single "back" click doesn't wipe the query param, it actually navs to the previous page
  async createCaseId() {
    let caseResponse = await this.mugCaseService.createCaseId(this.userId, this.agency.agencyId);

    if (caseResponse.caseId == null) {
      throw('No case id in create case response');
    }

    this.caseId = caseResponse.caseId.toString();
    history.replaceState(null, null, window.location.pathname + '?caseId=' + this.caseId);
  }

  async loadEnrollments() {
    this.isLoading = true;

    const enrollmentRequestResponse: EnrollmentResponsePayload = await this.mugCaseService.loadEnrollmentsForCaseId(this.caseId,  this.agency.agencyId);
    const enrollments = enrollmentRequestResponse.enrollments;
    let formIndex = 0;

    // Include dependents in total count of rows
    this.totalRowsToProcess = 0;
    enrollments.forEach((enrollment) => {
      this.totalRowsToProcess += (enrollment.applicant?.dependents?.length ?? 0) + 1;
    });

    for (let enrollmentIndex = 0; enrollmentIndex < enrollments.length; enrollmentIndex++) {

      const enrollment = enrollments[enrollmentIndex];

      // Before working data for grid, make some changes to the response payload to support mass-upload-grid formControls for attestations
      this.mugSubmissionDataService.handleResponseDataSignatureAttestations(enrollments[enrollmentIndex]);

      // Flatten - subscriber
      let flattenedEnrollmentData = this.mugSubmissionDataService.flattenObject(enrollment); // Flattens TOP-LEVEL APPLICANT ( replaces values if dependents have the same formControlNames ); DEPENDENTS not FLATTENED
      // console.log("Flattened Enrollment ( previously submitted ): ", flattenedEnrollmentData);
      
      await this.mugRowActionService.addNewRow(formIndex, EXCEL_PERSON_TYPES.SUBSCRIBER, enrollment.enrollmentType, flattenedEnrollmentData);
      formIndex++;

      for (let dependentIndex = 0; dependentIndex < enrollment.applicant?.dependents?.length; dependentIndex++) {
        const dependent = enrollment.applicant.dependents[dependentIndex];
        // Flatten - dependent
        const flattenedDependent = this.mugSubmissionDataService.flattenObject(dependent);
        await this.mugRowActionService.addNewRow(formIndex, PAYLOAD_TO_EXCEL_MAP[dependent.relationship], enrollment.enrollmentType, flattenedDependent);
        formIndex++;
      }
    }

    // No need to load any data or process controls if case is already submitted
    if (enrollmentRequestResponse.caseStatus !== CASE_STATUS.IN_PROGRESS) {
      this.errorMessage = 'This case is already submitted. You can no longer edit the enrollments.';
      this.isSubmittedCase = true;
    }
    else {
      await this.loadAndInitializeAllDataAndValidation();
    }

    this.resetTableHeight();
    this.updateScrollableElements();
    
    this.isLoading = false;
    this.totalRowsToProcess = 0;
  }

  patchEnrollmentDataToRow(enrollmentIndex: number, flattenedEnrollmentData: any) {
    let formGroups = this.gridFormMap[enrollmentIndex].formGroups;
    Object.keys(formGroups).forEach((formKey) => {
      formGroups[formKey].patchValue(flattenedEnrollmentData);
    });
  }

  resetTableHeight() {
    // Update the table body container height
    this.tableBodyContainer.nativeElement.style = 'height: ' + (this.gridEntryList.length * this.DATA_ROW_HEIGHT) + 'px; ';
    this.tableBodyContainer.nativeElement.style += 'min-height: ' + (this.gridEntryList.length * this.DATA_ROW_HEIGHT) + 'px;';
    this.tableBodyContainer.nativeElement.style += 'max-height: ' + (this.gridEntryList.length * this.DATA_ROW_HEIGHT) + 'px';
  }

  updateScrollableElements() {
    if (!this.gridScrollContainer) {
      return;
    }

    // This must be set based on the actual viewable area of the grid, and what looks best for the user. May require trial and error
    let visibleRows = 8;

    // Calculate which rows should be visible
    const scrollTop = this.gridScrollContainer.nativeElement.scrollTop;
    const startIndex = Math.floor(scrollTop / this.DATA_ROW_HEIGHT);
    this.visibleStartIndex = Math.max(0, startIndex);
    this.visibleEndIndex = Math.min(this.gridEntryList.length, startIndex + visibleRows);

    //this.clearEditRowAndSyncIndex();

    this.changeDetection.detectChanges();
  }

  getErrorString(entryRowIndex: number, entry: MassUploadEntryConfig) {
    let formControlName = entry.formControlAttributes.formControlName;
    let connectPath = entry.formControlAttributes.connectPath;
    let formControl = this.gridFormMap[entryRowIndex].formGroups[connectPath].controls[formControlName];

    if (!formControl?.errors) {
      return '';
    }

    let customErrors = this.formJsonToExcelMap.find((value) => value.formControlName === formControlName)?.errorMessages;
    let errors = Object.keys(formControl.errors);
    if (errors?.length) {
      return customErrors?.[errors[0]] ?? this.defaultErrorMessages[errors[0]] ?? errors[0];
    }

    return '';
  }

  async downloadCurrentGridDataToExcel() {
    this.isLoading = true;
    
    try {
      let downloadResponse = await this.mugCaseService.downloadExcelForCase(this.caseId, this.userId, this.agency.agencyId);
      this.mugSubmissionDataService.saveByteArrayAsExcel(`EnrollmentGrid-${this.caseId}.xlsx`, downloadResponse);
    }
    catch(e) {
      console.error('Error downloading grid', e);
    }

    this.isLoading = false;
  }
}

